import { Col, Row, Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import NewModal from "../../shared/Modals/NewModal";
import { useState, useEffect, useContext } from "react";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import { canAccessAllPlatforms } from "@serie3/common/config/RoleRights";
import NewButtonWithIcon from "../../shared/Buttons/NewButtonWithIcon";
import crudApi from "@serie3/common/api/crudApi";
import { useFormik } from "formik";
import ScheduleForm from "@serie3/mymediaplanner/Modules/Social/Forms/ScheduleForm";
import Schedule from "@serie3/mymediaplanner/Modules/Social/Entity/Schedule";
import DateTimeField from "../../Form/DateTimeField";
import SaveButton from "../../shared/Buttons/SaveButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin, faYoutube, faMeta } from "@fortawesome/free-brands-svg-icons";
import { faCheckCircle, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import ToastMessages from "../../../ToastMessages";
import TextField from "../../Form/TextField";
import Timezones from "../../Form/Timezones";
import PostCalendar from "../PostCalendar";
import YoutubeIcon from "../../shared/YoutubeIcon";
import ScheduleTable from "./Schedules/ScheduleTable";
import ScheduleFormView from "./Schedules/ScheduleFormView";
import moment from "moment";

const ALL_PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: faLinkedin, color: "#0077b5" },
  { key: "youtube",  label: "YouTube",  icon: faYoutube,  color: "#ff0000" },
  { key: "meta",     label: "Meta",     icon: faMeta,     color: "#0081fb" },
];

const PlatformMultiSelect = ({ formik, label, allPlatforms }) => {
  const platforms = allPlatforms ? ALL_PLATFORMS : ALL_PLATFORMS.filter((p) => p.key === "linkedin");
  const selected = formik.values.platforms ?? [];
  const toggle = (key) => {
    if (selected.includes(key)) {
      formik.setFieldValue("platforms", selected.filter((p) => p !== key));
    } else {
      formik.setFieldValue("platforms", [...selected, key]);
    }
  };
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 6, display: "block" }}>
        {label}
      </label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {platforms.map(({ key, label: platformLabel, icon, color }) => {
          const isSelected = selected.includes(key);
          return (
            <button key={key} type="button" onClick={() => toggle(key)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                gap: 8, padding: "10px 16px", borderRadius: 10, cursor: "pointer",
                border: isSelected ? `2px solid ${color}` : "2px solid #e0e4ea",
                background: isSelected ? `${color}18` : "#fff",
                color: isSelected ? color : "#9299a8",
                transition: "all 0.15s ease", minWidth: 80,
              }}
            >
              {key === "youtube"
                ? <YoutubeIcon height={22} />
                : <FontAwesomeIcon icon={icon} style={{ fontSize: 22 }} />}
              <span style={{ fontSize: 11, fontWeight: 600 }}>{platformLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const PublishNowModal = ({ show, onHide, onConfirm, allPlatforms, isPublishing }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(["linkedin"]);
  const platforms = allPlatforms ? ALL_PLATFORMS : ALL_PLATFORMS.filter((p) => p.key === "linkedin");

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered onShow={() => setSelected(["linkedin"])}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          {t("Backend.Post.Publish")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
          {t("Backend.Post.SelectPlatforms", "Select platforms to publish to")}
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {platforms.map(({ key, label, icon, color }) => {
            const isSelected = selected.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggle(key)}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  gap: 6, padding: "12px 18px", borderRadius: 12, cursor: "pointer",
                  border: isSelected ? `2px solid ${color}` : "2px solid #e0e4ea",
                  background: isSelected ? `${color}18` : "#fff",
                  color: isSelected ? color : "#9299a8",
                  transition: "all 0.15s ease", minWidth: 85,
                }}
              >
                {key === "youtube"
                  ? <YoutubeIcon height={24} />
                  : <FontAwesomeIcon icon={icon} style={{ fontSize: 24 }} />}
                <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
              </button>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} style={{ borderRadius: 8, fontSize: 13 }}>
          {t("Backend.Buttons.Cancel", "Cancel")}
        </Button>
        <Button
          onClick={() => onConfirm(selected)}
          disabled={selected.length === 0 || isPublishing}
          style={{ borderRadius: 8, fontSize: 13, background: "#038edc", border: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
          {t("Backend.Post.Publish")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * `onPostChanged(post)` gets the post as the API now sees it after anything in
 * this tab changed its status: set ready, a plan created / edited / deleted,
 * publish now. The post editor passes it on to the board that opened it.
 */
const SchedulePostTab = ({ post, flushSave = null, onPostChanged = null }) => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);
  const allPlatforms = canAccessAllPlatforms(settings?.user?.roles);
  const [showPublished, setShowPublished] = useState(false);
  const [showPlanningForm, setshowPlanningForm] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [editData, setEditData] = useState();
  const [postStatus, setPostStatus] = useState(post.status);
  // The editor swaps in the fresh post after a change; follow its status.
  useEffect(() => {
    setPostStatus(post.status);
  }, [post.status]);

  /** Reads the post back and hands it to the board; the API decides the status. */
  const refreshPost = async () => {
    if (typeof onPostChanged !== "function") return;
    const response = await crudApi.justGet(SocialEndpoints.posts.show + post.id);
    if (response.ok && response.data?.id) onPostChanged(response.data);
  };
  const [calendarSchedules, setCalendarSchedules] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [showPublishNow, setShowPublishNow] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  // Group members this post may be planned for; empty unless the post is shared in a group.
  const [planForCandidates, setPlanForCandidates] = useState([]);

  useEffect(() => {
    let cancelled = false;
    crudApi
      .justGet(SocialEndpoints.posts.schedule.planForCandidates + post.id)
      .then((r) => { if (!cancelled && r.ok) setPlanForCandidates(r.data?.candidates ?? []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [post.id]);

  const fetchCalendarSchedules = async (date) => {
    const month = moment(date).format("MMYYYY");
    await crudApi.getAll(
      SocialEndpoints.posts.schedule.index + month,
      setCalendarSchedules,
      SocialEndpoints.posts.scheduledDataField
    );
  };

  useEffect(() => {
    fetchCalendarSchedules(calendarDate);
  }, [calendarDate]);

  const scheduleForm = ScheduleForm.createScheduleForm(
    dataList,
    setDataList,
    null,
    null,
    post.id,
    // a new plan moves the post to Planned; an edit may change what the card shows
    () => refreshPost()
  );

  const formik = useFormik(scheduleForm);

  const addFunction = () => {
    setEditData(null);
    setshowPlanningForm(true);
  };

  const setPublishedFunction = () => {
    setEditData(null);
    setShowPublished(true);
  };

  const Save = () => {
    return <SaveButton formik={formik} />;
  };

  useEffect(() => {
    const fetchSchedules = async () => {
      await crudApi.getAll(
        SocialEndpoints.posts.schedule.indexAllForPost + post.id,
        setDataList,
        SocialEndpoints.posts.scheduledDataField
      );
    };

    fetchSchedules();
  }, [post.id]);

  const setDraftFalse = async () => {
    const response = await crudApi.justGet(
      SocialEndpoints.posts.setReady + post.id
    );
    if (response.ok) {
      setPostStatus("ready");
      // setready answers with the post itself; ready or, with a future plan, scheduled
      if (typeof onPostChanged === "function" && response.data?.id) onPostChanged(response.data);
    }
  };

  // Publish-now flow: pick platforms -> mark the post ready -> on 200 publish it.
  const publishNow = async (platforms) => {
    setIsPublishing(true);
    try {
      // 0. Write unsaved form changes first: the publish reads title and text from
      //    the database, and a title that is not saved yet is not burned into the image.
      if (flushSave && !(await flushSave())) {
        ToastMessages.Error();
        return;
      }

      // 1. Mark the post ready.
      const readyResponse = await crudApi.justGet(
        SocialEndpoints.posts.setReady + post.id
      );
      if (!readyResponse.ok) {
        ToastMessages.Error();
        return;
      }
      setPostStatus("ready");

      // 2. Ready came back 200 -> publish to the selected platforms.
      const publishResponse = await crudApi.createNew(
        SocialEndpoints.posts.publish + post.id,
        { platforms }
      );
      if (publishResponse.ok) {
        setPostStatus("published");
        setShowPublishNow(false);
      }
      // publish answers with per-platform results, not the post: read it back
      // (published, or error when a platform failed)
      await refreshPost();
    } catch (e) {
      console.error(e);
      ToastMessages.Error();
    } finally {
      setIsPublishing(false);
    }
  };

  return postStatus === "ready" ||
    postStatus === "scheduled" ||
    postStatus === "published" ? (
    <>
      <ScheduleFormView
        formik={formik}
        showPlanningForm={showPlanningForm}
        setshowPlanningForm={setshowPlanningForm}
        editData={editData}
        allPlatforms={allPlatforms}
        candidates={planForCandidates}
      />
      <NewModal
        title={t("Backend.Post.SetPublished")}
        show={showPublished}
        setShow={setShowPublished}
        onExit={() => {
          formik.resetForm({ values: { Schedule } });
        }}
        onShow={() => {
          editData
            ? formik.setValues(ScheduleForm.setEditData(editData))
            : formik.setValues(Schedule);
        }}
        ExtraButton={Save}
      >
        <Row className="mt-5">
          <Col>
            <DateTimeField
              formik={formik}
              label={t("Backend.Post.Schedule.PostedAt")}
              valueName={"posted_at"}
            />
          </Col>
          <Col>
            <Timezones formik={formik} />
          </Col>
          <Col>
            <TextField
              formik={formik}
              label={t("Backend.Post.PortalId")}
              valueName={"portal_post_id"}
              style={{ marginBottom: "10px" }}
            />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <PlatformMultiSelect
              formik={formik}
              label={t("Backend.Post.Schedule.Platformname")}
              allPlatforms={allPlatforms}
            />
          </Col>
        </Row>
      </NewModal>
      <Row>
        <Col>
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 style={{ fontWeight: 700, color: "#1a2236", margin: 0 }}>
              {t("Backend.Post.Schedule.ScheduleTitle")}
            </h5>
            <div className="d-flex gap-2">
              <button
                onClick={addFunction}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "#038edc", color: "#fff",
                  fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 12,
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#026aab"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#038edc"; }}
              >
                {t("Backend.Post.Schedule.Plan")}
              </button>
            </div>
          </div>
          <ScheduleTable
            dataList={dataList}
            setDataList={setDataList}
            setEditData={setEditData}
            setshowPlanningForm={setshowPlanningForm}
            schedules={dataList}
            setSchedules={setDataList}
            onDeleted={() => refreshPost()}
          />
        </Col>
        <Col>
          <h5 style={{ fontWeight: 700, color: "#1a2236", marginBottom: 16 }}>
            {t("Backend.Post.Schedule.CalendarTitle")}
          </h5>
          <PostCalendar
            schedules={calendarSchedules}
            setSchedules={setCalendarSchedules}
            dontUseCalendarClick={true}
            date={calendarDate}
            onMonthChange={(newDate) => setCalendarDate(moment(newDate).startOf("month").toDate())}
            onPlanSaved={() => fetchCalendarSchedules(calendarDate)}
          />
        </Col>
      </Row>
    </>
  ) : (
    <>
      <h3>{t("Backend.Post.Schedule.MarkPostDoneFirst")}</h3>
      <div className="d-flex align-items-center gap-2">
        <Button
          type="button"
          className="btn btn-success d-flex align-items-center"
          onClick={() => {
            setDraftFalse();
          }}
        >
          {t("Backend.Post.SetReady")}
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="ms-2" // Adds a small margin to the left of the icon
          />
        </Button>
        <Button
          type="button"
          className="btn btn-primary d-flex align-items-center"
          onClick={() => setShowPublishNow(true)}
        >
          {t("Backend.Post.PublishNow", "Publish now")}
          <FontAwesomeIcon icon={faPaperPlane} className="ms-2" />
        </Button>
      </div>
      <PublishNowModal
        show={showPublishNow}
        onHide={() => setShowPublishNow(false)}
        onConfirm={publishNow}
        allPlatforms={allPlatforms}
        isPublishing={isPublishing}
      />
    </>
  );
};

export default SchedulePostTab;
